import React, { forwardRef } from "react";
import { KPIValue, type KPIValueProps } from "../../atoms/KPIValue/index.js";
import { Card, CardBody } from "../Card/index.js";

/* ---------------- StatTile ---------------- */

/** `default` is a card on the page. `inset` is the recessed well (Card
 *  variant="inset" size="sm") for a tile inside another card; never nest a
 *  `default` tile in a card. */
export type StatTileVariant = "default" | "inset";

/* A numeric trend (delta, with its inverse and unit) or a slot in its place,
   never both: KPIValue draws one or the other. */
type StatTileTrendProps =
  | {
      delta?: number;
      inverse?: boolean;
      deltaUnit?: React.ReactNode;
      deltaSlot?: never;
    }
  | {
      deltaSlot: React.ReactNode;
      delta?: never;
      inverse?: never;
      deltaUnit?: never;
    };

interface StatTileBaseProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Default `default`. */
  variant?: StatTileVariant;
  /** KPIValue's label; the tile's heading text. */
  label: React.ReactNode;
  /** KPIValue's figure. Omitted: a status-only tile with no empty figure. */
  value?: React.ReactNode;
  unit?: KPIValueProps["unit"];
  /** A muted caption under the value (KPIValue's description). */
  description?: KPIValueProps["description"];
  /** Colours the value only (KPIValue's tone). */
  tone?: KPIValueProps["tone"];
  /** The figure's scale (KPIValue's size). Default `md`. */
  size?: KPIValueProps["size"];
  /** Decorative glyph at the top-end corner. Hidden from assistive tech, so
   *  the label must carry the meaning. */
  icon?: React.ReactNode;
  /** A status under the value, such as a StatusPill. */
  statusSlot?: React.ReactNode;
  /** Controls rendered last, such as a Button. Each needs its own name. */
  actionsSlot?: React.ReactNode;
}

export type StatTileProps = StatTileBaseProps & StatTileTrendProps;

/* The LAMA StatCard placement: 16px, top-end, muted. The svg is sized here so
   an icon element passed without classes matches it. */
const ICON = "absolute end-3 top-3 h-4 w-4 text-pb-text-muted [&_svg]:size-4";

/* A slot is its own row, and its children line up in it. */
const SLOT_ROW = "flex flex-wrap items-center gap-2";

const present = (node: React.ReactNode) => node != null && node !== false;

/**
 * One figure on a card: Card > CardBody > KPIValue, plus an optional corner
 * icon, a status row and an actions row. The surface and its padding come
 * only from Card (`variant` picks the recipe); the label and value typography
 * come only from KPIValue. StatTile adds layout classes and nothing else.
 */
export const StatTile = forwardRef<HTMLDivElement, StatTileProps>(
  function StatTile(
    {
      variant = "default",
      label,
      value,
      unit,
      description,
      tone,
      size,
      delta,
      inverse,
      deltaUnit,
      deltaSlot,
      icon,
      statusSlot,
      actionsSlot,
      ...rest
    },
    ref,
  ) {
    const inset = variant === "inset";
    return (
      <Card
        ref={ref}
        {...rest}
        variant={inset ? "inset" : "default"}
        size={inset ? "sm" : "md"}
      >
        <CardBody className="relative space-y-1">
          {present(icon) && (
            <span aria-hidden="true" className={ICON}>
              {icon}
            </span>
          )}
          <KPIValue
            label={label}
            value={value}
            unit={unit}
            description={description}
            tone={tone}
            size={size}
            deltaUnit={deltaUnit}
            deltaSlot={deltaSlot}
            {...(delta !== undefined ? { delta } : {})}
            {...(inverse !== undefined ? { inverse } : {})}
          />
          {present(statusSlot) && <div className={SLOT_ROW}>{statusSlot}</div>}
          {present(actionsSlot) && (
            <div className={SLOT_ROW}>{actionsSlot}</div>
          )}
        </CardBody>
      </Card>
    );
  },
);
